import { MouseEvent, useEffect, useState } from 'react';

import style from './SupportWidget.module.scss';

import type { SupportAttachment } from 'chaika-frontend-types/frontend-cms';

import AttachedIcon from '@components/Icon/svg/attached.svg';

import { shortenFileName } from './utils/fileName';

interface ReceivedSupportAttachmentProps {
    attachment: SupportAttachment;
    onOpen(event: MouseEvent<HTMLAnchorElement>, attachment: SupportAttachment): void;
    onUrlExpired(): void;
}

export function ReceivedSupportAttachment({ attachment, onOpen, onUrlExpired }: ReceivedSupportAttachmentProps) {
    const [failedPreviewUrl, setFailedPreviewUrl] = useState<string>();
    const canShowPreview = attachment.mimeType.startsWith('image/') && failedPreviewUrl !== attachment.url;

    return (
        <a
            className={canShowPreview ? undefined : style.messageAttachmentLink}
            href={attachment.url}
            target={'_blank'}
            rel={'noopener noreferrer'}
            aria-label={attachment.fileName}
            onClick={event => onOpen(event, attachment)}
        >
            {canShowPreview ? (
                <img
                    className={style.messageAttachmentImage}
                    src={attachment.url}
                    alt={attachment.fileName}
                    loading={'lazy'}
                    onError={() => {
                        setFailedPreviewUrl(attachment.url);
                        onUrlExpired();
                    }}
                />
            ) : (
                <>
                    <AttachedIcon aria-hidden={'true'} />
                    <SupportAttachmentFileName fileName={attachment.fileName} />
                </>
            )}
        </a>
    );
}

interface LocalSupportAttachmentPreviewProps {
    file: File;
}

export function LocalSupportAttachmentPreview({ file }: LocalSupportAttachmentPreviewProps) {
    const [previewUrl] = useState(() => (file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined));

    useEffect(
        () => () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        },
        [previewUrl],
    );

    return previewUrl ? (
        <img
            className={style.localAttachmentPreview}
            src={previewUrl}
            alt={file.name}
        />
    ) : (
        <SupportAttachmentFileName
            className={style.localAttachmentFile}
            fileName={file.name}
        />
    );
}

interface SupportAttachmentFileNameProps {
    className?: string;
    fileName: string;
}

export function SupportAttachmentFileName({ className, fileName }: SupportAttachmentFileNameProps) {
    const { beginning, ending } = shortenFileName(fileName);

    return (
        <span
            className={className}
            title={fileName}
        >
            {beginning}
            <span className={style.attachmentFileNameEnding}>{ending}</span>
        </span>
    );
}
